import { useCallback, useRef, useState } from "react"

const CLOSE_DELAY_MS = 150

type UseHoverMenuReturn = Readonly<{
    anchorEl: HTMLElement | null
    isOpen: boolean
    handleOpen: (event: React.MouseEvent<HTMLElement>) => void
    handleClose: () => void
    scheduleClose: () => void
    cancelClose: () => void
}>

export function useHoverMenu(): UseHoverMenuReturn {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

    const cancelClose = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current)
            closeTimeout.current = null
        }
    }, [])

    const scheduleClose = useCallback(() => {
        cancelClose()
        closeTimeout.current = setTimeout(() => {
            setAnchorEl(null)
        }, CLOSE_DELAY_MS)
    }, [cancelClose])

    const handleOpen = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            cancelClose()
            setAnchorEl(event.currentTarget)
        },
        [cancelClose],
    )

    const handleClose = useCallback(() => {
        cancelClose()
        setAnchorEl(null)
    }, [cancelClose])

    return {
        anchorEl,
        isOpen: Boolean(anchorEl),
        handleOpen,
        handleClose,
        scheduleClose,
        cancelClose,
    }
}
